import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import Layout from './components/Layout/Layout';
import DashboardPage from './pages/DashboardPage';
import RunsPage from './pages/RunsPage';
import RunCalendarPage from './pages/RunCalendarPage';
import RunDetailsPage from './pages/RunDetailsPage';
import LogsPage from './pages/LogsPage';
import ParticipantsPage from './pages/ParticipantsPage';
import StatusPage from './pages/StatusPage';
import WorkloadsPage from './pages/WorkloadsPage';
import WorkloadEditorPage from './pages/WorkloadEditorPage';
import WorkloadProfilePage from './pages/WorkloadProfilePage';
import InstallersPage from './pages/InstallersPage';
import { BuildLogsPage } from './pages/BuildLogsPage';
import ImageBuildsPage from './pages/ImageBuildsPage';
import ImageBuildDetailPage from './pages/ImageBuildDetailPage';
import NewImageWizardPage from './pages/NewImageWizardPage';
import AppCatalogPage from './pages/AppCatalogPage';
import AppRegisterWizardPage from './pages/AppRegisterWizardPage';
import AuditPage from './pages/AuditPage';
import AzureResourcesPage from './pages/AzureResourcesPage';
import DriftPage from './pages/DriftPage';
import EventsPage from './pages/EventsPage';
import ChangelogPage from './pages/ChangelogPage';
import UsersPage from './pages/UsersPage';
import RoomsPage from './pages/RoomsPage';
import CostCentersPage from './pages/CostCentersPage';
import FinOpsPage from './pages/FinOpsPage';
import WikiPage from './pages/WikiPage';
import HelpPage from './pages/HelpPage';
import SettingsPage from './pages/SettingsPage';
import NavigationEditorPage from './pages/NavigationEditorPage';
import LoginPage from './pages/LoginPage';
import ParticipantPage from './pages/ParticipantPage';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { NavConfigProvider } from './contexts/NavConfigContext';
import ErrorBoundary from './components/ErrorBoundary';
import { NC_972_WIZARD_ENABLED } from './config/featureFlags';
import theme from './theme/theme';

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <ErrorProvider>
          <NavConfigProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* NC-1101: öffentliche Teilnehmerseite (Magic-Link, kein Layout/Auth) */}
              <Route path="/teilnehmer" element={<ParticipantPage />} />
              <Route path="/" element={<Layout />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="runs" element={<RunsPage />} />
                {/* NC-1269: React Router v6 ranks the static segment above the
                    dynamic one, so "kalender" is not swallowed by runs/:runId
                    regardless of declaration order — it sits here for
                    readability, not because the order is load-bearing. */}
                <Route path="runs/kalender" element={<RunCalendarPage />} />
                <Route path="runs/:runId" element={<RunDetailsPage />} />
                <Route path="builds" element={<BuildLogsPage />} />
                <Route path="logs" element={<LogsPage />} />
                <Route path="participants" element={<ParticipantsPage />} />
                <Route path="workloads" element={<WorkloadsPage />} />
                {/* NC-1291: the admin-only "Neuer Workload" button navigates here.
                    Without this route the click rendered an empty Outlet and workload
                    creation via UI was dead. Static path outranks :workloadId (same
                    convention as images/new above); WorkloadEditorPage reads no
                    workloadId param here, so isEditMode stays false = create mode. */}
                <Route path="workloads/new" element={<WorkloadEditorPage />} />
                <Route path="workloads/:workloadId/edit" element={<WorkloadEditorPage />} />
                <Route path="workloads/:workloadId/steckbrief" element={<WorkloadProfilePage />} />
                <Route path="workloads/:workloadId/builds" element={<ImageBuildsPage />} />
                <Route path="images" element={<ImageBuildsPage />} />
                {/* NC-972: gated behind NC_972_WIZARD_ENABLED (default off) until
                    all wizard steps are built. Static path outranks :buildId. */}
                {NC_972_WIZARD_ENABLED && (
                  <Route path="images/new" element={<NewImageWizardPage />} />
                )}
                <Route path="images/:buildId" element={<ImageBuildDetailPage />} />
                <Route path="installers" element={<InstallersPage />} />
                {/* Image Builder 2.1 Phase C (#1712). Static path outranks nothing here. */}
                <Route path="apps" element={<AppCatalogPage />} />
                <Route path="apps/register" element={<AppRegisterWizardPage />} />
                <Route path="status" element={<StatusPage />} />
                <Route path="neuigkeiten" element={<ChangelogPage />} />
                <Route path="audit" element={<AuditPage />} />
                <Route path="azure-resources" element={<AzureResourcesPage />} />
                <Route path="admin/drift" element={<DriftPage />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="rooms" element={<RoomsPage />} />
                <Route path="cost-centers" element={<CostCentersPage />} />
                <Route path="finops" element={<FinOpsPage />} />
                <Route path="wiki" element={<WikiPage />} />
                <Route path="hilfe" element={<HelpPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="admin/navigation" element={<NavigationEditorPage />} />
              </Route>
            </Routes>
          </Router>
          </NavConfigProvider>
          </ErrorProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
